import { FeatureType } from 'src/shared/types';
import { GraphProject } from 'src/app/admin/state/api/queries/projectAdminQuery';

/** The definition fields the merged table displays. Shaped by projects.projectList rows. */
export type ProjectDefinitionRow = {
    projectId: string;
    projectName?: string | null;
    projectCode?: string | null;
    country?: string | null;
    status?: string | null;
    samplesCount?: number | null;
    features?: FeatureType[] | null;
};

export type MergedProjectRow = {
    projectId: string;
    projectName: string | null;
    projectCode: string | null;
    leadName: string | null;
    leadEmail: string | null;
    hasDefinition: boolean;
    definition: ProjectDefinitionRow | null;
};

/**
 * Joins the graph's projects against their definitions.
 *
 * The graph is the source of truth for which projects exist — `admin.createProject`
 * writes there and nowhere else, so a project awaiting its definition appears only
 * here. Definitions are therefore attached where they exist and their absence is
 * reported rather than hiding the row.
 *
 * A definition with no matching project is dropped: it cannot be reached or acted on
 * through this page, and the domain rule is that a definition requires a project first.
 */
export const mergeProjectsWithDefinitions = (graphProjects: GraphProject[], definitions: ProjectDefinitionRow[]): MergedProjectRow[] => {
    const definitionsByProjectId = new Map(definitions.map(definition => [definition.projectId, definition]));

    return graphProjects.map(project => {
        const definition = definitionsByProjectId.get(project.projectId) ?? null;

        return {
            projectId: project.projectId,
            projectName: project.projectName ?? definition?.projectName ?? null,
            projectCode: project.projectCode ?? definition?.projectCode ?? null,
            leadName: project.leadName,
            leadEmail: project.leadEmail,
            hasDefinition: definition !== null,
            definition,
        };
    });
};
